<div class="vp-page">
  <div class="vp-grid" [ngStyle]="{'grid-template-columns': isMentorView ? '1fr' : '1fr 340px'}">

    <!-- ═══════════════════════ MAIN COLUMN ═══════════════════════ -->
    <div class="vp-main">

      <!-- ─── Header card ─────────────────────────────────────────── -->
      <section class="vp-header-card" *ngIf="!isMentorView">
        <div class="vp-header-top">
          <ng-container *ngIf="loadingUser; else headerContent">
            <div class="vp-header-loading">
              <p-skeleton shape="circle" size="6rem" styleClass="vp-sk-light"></p-skeleton>
              <div class="flex-1">
                <p-skeleton width="14rem" height="1.6rem" styleClass="vp-sk-light mb-2"></p-skeleton>
                <p-skeleton width="20rem" height="1rem" styleClass="vp-sk-light"></p-skeleton>
              </div>
            </div>
          </ng-container>

          <ng-template #headerContent>
            <img *ngIf="user?.imageUrl" class="vp-avatar vp-avatar-img" [src]="user?.imageUrl" alt="Profile photo" />
            <div *ngIf="!user?.imageUrl" class="vp-avatar">{{ initials || 'GP' }}</div>
            <div class="vp-header-info">
              <div class="vp-name-row">
                <h1 class="vp-name">{{ fullName || 'Student' }}</h1>
                <button *ngIf="resolvedCanEdit" type="button" class="vp-edit-profile-btn"
                  (click)="openPersonalDialog()">
                  <i class="pi pi-pencil"></i> Edit Profile
                </button>
              </div>
              <div class="vp-badges">
                <span class="vp-badge" *ngIf="genderLabel"><i class="pi pi-user"></i>{{ genderLabel }}</span>
                <span class="vp-badge" *ngIf="user?.bornOn"><i class="pi pi-calendar"></i>{{ formatDob(user?.bornOn)
                  }}</span>
                <span class="vp-badge" *ngIf="user?.collageStartOn"><i class="pi pi-clock"></i>{{ user?.collageStartOn
                  }}</span>
              </div>
            </div>
          </ng-template>
        </div>

        <!-- Personal detail grid — only fields we actually have a value for -->
        <div class="vp-detail-grid" *ngIf="!loadingUser">
          <div class="vp-detail" *ngIf="homeCountryName">
            <span class="vp-detail-label">Home Country</span>
            <span class="vp-detail-value">{{ homeCountryName }}</span>
          </div>
          <div class="vp-detail" *ngIf="user?.homeCity || user?.location">
            <span class="vp-detail-label">{{ user?.homeCity ? 'City' : 'Location' }}</span>
            <span class="vp-detail-value">{{ user?.homeCity || user?.location }}</span>
          </div>
          <div class="vp-detail" *ngIf="intendedCountriesLabel">
            <span class="vp-detail-label">Intended Countries</span>
            <span class="vp-detail-value" [pTooltip]="intendedCountriesTooltip" tooltipPosition="top">
              {{ intendedCountriesLabel }}
              <small *ngIf="intendedCountriesExtra" class="vp-more">+{{ intendedCountriesExtra }} More</small>
            </span>
          </div>
          <div class="vp-detail" *ngIf="intendedLevelLabel">
            <span class="vp-detail-label">Intended Level</span>
            <span class="vp-detail-value">{{ intendedLevelLabel }}</span>
          </div>
          <div class="vp-detail" *ngIf="user?.intendedMajor">
            <span class="vp-detail-label">Intended Major</span>
            <span class="vp-detail-value">{{ user?.intendedMajor }}</span>
          </div>
          <div class="vp-detail" *ngIf="user?.journeyStatus">
            <span class="vp-detail-label">Journey Stage</span>
            <span class="vp-detail-value">{{ user?.journeyStatus }}</span>
          </div>
          <div class="vp-detail" *ngIf="hsGraduationYear">
            <span class="vp-detail-label">HS Graduation</span>
            <span class="vp-detail-value">{{ hsGraduationYear }}</span>
          </div>
        </div>
      </section>

      <!-- ─── Education ───────────────────────────────────────────── -->
      <section class="vp-section">
        <div class="vp-section-head">
          <h2>Education</h2>
          <button *ngIf="resolvedCanEdit && educations.length" type="button" class="vp-edit-mini vp-edit-onhead"
            (click)="openEducationDialog()">
            <i class="pi pi-pencil"></i> Edit
          </button>
        </div>
        <div class="vp-section-body">
          <!-- loading -->
          <ng-container *ngIf="loadingEducation">
            <div class="vp-block" *ngFor="let s of skeletonRows">
              <p-skeleton width="60%" height="1.1rem" styleClass="mb-2"></p-skeleton>
              <p-skeleton width="40%" height="0.9rem" styleClass="mb-3"></p-skeleton>
              <div class="flex gap-2">
                <p-skeleton width="6rem" height="1.6rem"></p-skeleton>
                <p-skeleton width="6rem" height="1.6rem"></p-skeleton>
                <p-skeleton width="6rem" height="1.6rem"></p-skeleton>
              </div>
            </div>
          </ng-container>

          <!-- empty -->
          <div class="vp-empty" *ngIf="!loadingEducation && educations.length === 0">
            <i class="pi pi-book"></i>
            <p>No education details added yet.</p>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-add-btn" (click)="openEducationDialog()">
              <i class="pi pi-plus"></i> Add Education
            </button>
          </div>

          <!-- content -->
          <ng-container *ngIf="!loadingEducation">
            <div class="vp-block" *ngFor="let edu of educations">
              <div class="vp-block-head">
                <span class="vp-block-tag">{{ edu.levelLabel }}</span>
              </div>
              <p class="vp-block-title">
                {{ edu.schoolName }}
                <ng-container *ngIf="edu.fromYear || edu.toYear"> | {{ edu.fromYear }}<span *ngIf="edu.toYear"> – {{
                    edu.toYear }}</span></ng-container>
                <ng-container *ngIf="edu.city"> | {{ edu.city }}</ng-container>
                <ng-container *ngIf="edu.boardName"> | {{ edu.boardName }}</ng-container>
              </p>
              <div class="vp-chips" *ngIf="edu.subjects.length">
                <span class="vp-chip" *ngFor="let s of edu.subjects">{{ s.name }} <b>{{ s.grade }}</b></span>
                <span class="vp-chip vp-chip-total" *ngIf="edu.totalGrade">Total <b>{{ edu.totalGrade }}</b></span>
              </div>
              <button *ngIf="edu.doc" type="button" class="vp-doc-link" (click)="openDoc(edu.doc)">
                <i class="pi pi-check-circle"></i> {{ edu.doc.name }}
              </button>
            </div>
          </ng-container>
        </div>
      </section>

      <!-- ─── Test Scores ─────────────────────────────────────────── -->
      <section class="vp-section">
        <div class="vp-section-head">
          <h2>Test Scores</h2>
          <button *ngIf="resolvedCanEdit && scoreTiles.length" type="button" class="vp-edit-mini vp-edit-onhead"
            (click)="openTestScoresDialog()">
            <i class="pi pi-pencil"></i> Edit
          </button>
        </div>
        <div class="vp-section-body">
          <ng-container *ngIf="loadingTestScores">
            <div class="vp-score-grid">
              <p-skeleton *ngFor="let s of [1,2,3,4]" height="4.5rem"></p-skeleton>
            </div>
          </ng-container>

          <div class="vp-empty" *ngIf="!loadingTestScores && scoreTiles.length === 0">
            <i class="pi pi-chart-bar"></i>
            <p>No test scores added yet.</p>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-add-btn" (click)="openTestScoresDialog()">
              <i class="pi pi-plus"></i> Add Test Scores
            </button>
          </div>

          <ng-container *ngIf="!loadingTestScores && scoreTiles.length">
            <div class="vp-score-grid">
              <div class="vp-score-tile" *ngFor="let t of scoreTiles">
                <span class="vp-score-label">{{ t.label }}</span>
                <span class="vp-score-value">{{ t.score }}</span>
                <span class="vp-score-outof">{{ t.outOf }}</span>
              </div>
            </div>
            <div class="vp-doc-row">
              <ng-container *ngFor="let t of scoreTiles">
                <button *ngIf="t.doc" type="button" class="vp-doc-link vp-doc-verified" (click)="openDoc(t.doc)">
                  <i class="pi pi-verified"></i> {{ t.doc.name }} verified
                </button>
              </ng-container>
            </div>
          </ng-container>
        </div>
      </section>

      <!-- ─── Experience ──────────────────────────────────────────── -->
      <section class="vp-section">
        <div class="vp-section-head">
          <h2>Experience</h2>
          <button *ngIf="resolvedCanEdit && experiences.length" type="button" class="vp-edit-mini vp-edit-onhead"
            (click)="openExperienceDialog()">
            <i class="pi pi-pencil"></i> Edit
          </button>
        </div>
        <div class="vp-section-body">
          <ng-container *ngIf="loadingExperience">
            <div class="vp-block" *ngFor="let s of skeletonRows">
              <p-skeleton width="50%" height="1.1rem" styleClass="mb-2"></p-skeleton>
              <p-skeleton width="35%" height="0.9rem" styleClass="mb-2"></p-skeleton>
              <p-skeleton width="90%" height="0.9rem"></p-skeleton>
            </div>
          </ng-container>

          <div class="vp-empty" *ngIf="!loadingExperience && experiences.length === 0">
            <i class="pi pi-briefcase"></i>
            <p>No experience added yet.</p>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-add-btn" (click)="openExperienceDialog()">
              <i class="pi pi-plus"></i> Add Experience
            </button>
          </div>

          <ng-container *ngIf="!loadingExperience">
            <div class="vp-block" *ngFor="let exp of experiences">
              <div class="vp-block-head">
                <div>
                  <p class="vp-exp-role">{{ exp.designation }}</p>
                  <p class="vp-exp-org">{{ exp.organisation }}</p>
                </div>
              </div>
              <p class="vp-exp-meta">
                <i class="pi pi-calendar"></i>
                {{ exp.fromLabel }}<span *ngIf="exp.toLabel"> – {{ exp.toLabel }}</span>
                <span *ngIf="exp.duration"> · {{ exp.duration }}</span>
                <span *ngIf="exp.location"> · {{ exp.location }}</span>
              </p>
              <p class="vp-exp-desc" *ngIf="exp.description">{{ exp.description }}</p>
              <button *ngIf="exp.doc" type="button" class="vp-doc-link" (click)="openDoc(exp.doc)">
                <i class="pi pi-check-circle"></i> {{ exp.doc.name }}
              </button>
            </div>
          </ng-container>
        </div>
      </section>

      <!-- ─── Recommendations ─────────────────────────────────────── -->
      <section class="vp-section">
        <div class="vp-section-head">
          <h2>Recommendations</h2>
          <button *ngIf="resolvedCanEdit && recommendations.length" type="button" class="vp-edit-mini vp-edit-onhead"
            (click)="openRecommendationsDialog()">
            <i class="pi pi-pencil"></i> Edit
          </button>
        </div>
        <div class="vp-section-body">
          <ng-container *ngIf="loadingRecommendations">
            <div class="vp-rec-row" *ngFor="let s of skeletonRows">
              <p-skeleton shape="circle" size="2.8rem"></p-skeleton>
              <div class="flex-1">
                <p-skeleton width="40%" height="1rem" styleClass="mb-2"></p-skeleton>
                <p-skeleton width="60%" height="0.85rem"></p-skeleton>
              </div>
            </div>
          </ng-container>

          <div class="vp-empty" *ngIf="!loadingRecommendations && recommendations.length === 0">
            <i class="pi pi-users"></i>
            <p>No recommendations added yet.</p>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-add-btn" (click)="openRecommendationsDialog()">
              <i class="pi pi-plus"></i> Add Recommendation
            </button>
          </div>

          <ng-container *ngIf="!loadingRecommendations">
            <div class="vp-rec-row" *ngFor="let rec of recommendations">
              <div class="vp-rec-avatar">{{ rec.initials }}</div>
              <div class="vp-rec-info">
                <p class="vp-rec-name">{{ rec.name }}</p>
                <p class="vp-rec-org" *ngIf="rec.organisation">{{ rec.organisation }}</p>
                <p class="vp-rec-contact">
                  <span *ngIf="rec.email"><i class="pi pi-envelope"></i>{{ rec.email }}</span>
                  <span *ngIf="rec.contact"><i class="pi pi-phone"></i>{{ rec.contact }}</span>
                </p>
                <button *ngIf="rec.doc" type="button" class="vp-doc-link" (click)="openDoc(rec.doc)">
                  <i class="pi pi-check-circle"></i> {{ rec.doc.name }}
                </button>
              </div>
            </div>
          </ng-container>
        </div>
      </section>

      <!-- ─── Awards & Accolades ──────────────────────────────────── -->
      <section class="vp-section">
        <div class="vp-section-head">
          <h2>Awards &amp; Accolades</h2>
          <button *ngIf="resolvedCanEdit && awards.length" type="button" class="vp-edit-mini vp-edit-onhead"
            (click)="openAwardsDialog()">
            <i class="pi pi-pencil"></i> Edit
          </button>
        </div>
        <div class="vp-section-body">
          <ng-container *ngIf="loadingAwards">
            <div class="vp-award-row" *ngFor="let s of skeletonRows">
              <p-skeleton shape="circle" size="2.4rem"></p-skeleton>
              <div class="flex-1">
                <p-skeleton width="45%" height="1rem" styleClass="mb-2"></p-skeleton>
                <p-skeleton width="80%" height="0.85rem"></p-skeleton>
              </div>
            </div>
          </ng-container>

          <div class="vp-empty" *ngIf="!loadingAwards && awards.length === 0">
            <i class="pi pi-star"></i>
            <p>No awards or accolades added yet.</p>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-add-btn" (click)="openAwardsDialog()">
              <i class="pi pi-plus"></i> Add Award
            </button>
          </div>

          <ng-container *ngIf="!loadingAwards">
            <div class="vp-award-row" *ngFor="let aw of awards">
              <div class="vp-award-icon"><i class="pi pi-star-fill"></i></div>
              <div class="vp-award-info">
                <p class="vp-award-name">{{ aw.name }}</p>
                <p class="vp-award-meta">
                  <span *ngIf="aw.categoryLabel">{{ aw.categoryLabel }}</span>
                  <span *ngIf="aw.levelLabel"> · {{ aw.levelLabel }}</span>
                  <span *ngIf="aw.issuer"> · {{ aw.issuer }}</span>
                </p>
                <p class="vp-award-desc" *ngIf="aw.description">{{ aw.description }}</p>
                <button *ngIf="aw.doc" type="button" class="vp-doc-link" (click)="openDoc(aw.doc)">
                  <i class="pi pi-check-circle"></i> {{ aw.doc.name }}
                </button>
              </div>
            </div>
          </ng-container>
        </div>
      </section>
    </div>

    <!-- ═══════════════════════ SIDEBAR ═══════════════════════ -->
    <aside class="vp-side" *ngIf="!isMentorView">

      <!-- ─── Profile completion ──────────────────────────────────── -->
      <div class="vp-card vp-completion-card">
        <div class="vp-completion-head">
          <div class="vp-ring">
            <svg viewBox="0 0 100 100">
              <circle class="vp-ring-bg" cx="50" cy="50" r="42"></circle>
              <circle class="vp-ring-fg" cx="50" cy="50" r="42" [attr.stroke-dasharray]="ringCircumference"
                [attr.stroke-dashoffset]="ringDashOffset"></circle>
            </svg>
            <span class="vp-ring-label">{{ completionPercentage }}%</span>
          </div>
          <div>
            <p class="vp-card-title">Profile Completion</p>
            <p class="vp-card-sub">{{ completedCount }} of {{ completionItems.length }} sections complete</p>
          </div>
        </div>

        <ul class="vp-checklist">
          <li *ngFor="let item of completionItems" [class.done]="item.done">
            <i class="pi" [ngClass]="item.done ? 'pi-check-circle' : 'pi-circle'"></i>
            {{ item.label }}
          </li>
        </ul>

        <ng-container *ngIf="resolvedCanEdit">
          <!-- <button type="button" class="vp-btn vp-btn-primary" (click)="onCompleteProfile()">Complete Profile</button> -->

          <button type="button" class="vp-btn vp-btn-primary" (click)="onCompleteProfile()">
            {{ completionPercentage >= 100 ? 'View Profile' : 'Complete Profile' }}
          </button>
          <button type="button" class="vp-btn vp-btn-outline" (click)="onScheduleClarityCall()">Schedule Clarity
            Call</button>
          <button type="button" class="vp-link-btn" (click)="onDirectApplication()">Direct Application Details</button>
        </ng-container>
      </div>

      <!-- ─── Mentor ──────────────────────────────────────────────── -->
      <div class="vp-card">
        <div class="vp-card-head">
          <p class="vp-card-title">Mentor</p>
          <span class="vp-pill" [class.vp-pill-muted]="!mentor && !loadingMentor">
            {{ loadingMentor ? '…' : (mentor ? 'Mentor Assigned' : 'No mentor assigned') }}
          </span>
        </div>

        <div class="vp-card-loading" *ngIf="loadingMentor">
          <p-skeleton shape="circle" size="3.2rem"></p-skeleton>
          <p-skeleton width="70%" height="1rem" styleClass="mt-2"></p-skeleton>
          <p-skeleton width="90%" height="2.4rem" styleClass="mt-3"></p-skeleton>
        </div>

        <ng-container *ngIf="!loadingMentor">
          <div class="vp-mentor" *ngIf="mentor; else noMentor">
            <div class="vp-mentor-avatar">{{ mentorInitials }}</div>
            <p class="vp-mentor-name">{{ mentorName }}</p>
            <p class="vp-mentor-sub" *ngIf="mentorSubtitle">{{ mentorSubtitle }}</p>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-btn vp-btn-outline"
              (click)="onMessageMentor()">Message Mentor</button>
          </div>
          <ng-template #noMentor>
            <div class="vp-placeholder">
              <div class="vp-placeholder-emoji">🧑‍🚀</div>
              <p>Connect with an expert mentor to accelerate your applications.</p>
              <button *ngIf="resolvedCanEdit" type="button" class="vp-btn vp-btn-outline" (click)="onFindMentor()">Find
                a Mentor</button>
            </div>
          </ng-template>
        </ng-container>
      </div>

      <!-- ─── Applications ────────────────────────────────────────── -->
      <div class="vp-card">
        <div class="vp-card-head">
          <p class="vp-card-title">Applications</p>
        </div>

        <div class="vp-card-loading" *ngIf="loadingApplications">
          <p-skeleton width="100%" height="2.4rem" styleClass="mb-2"></p-skeleton>
          <p-skeleton width="100%" height="2.4rem"></p-skeleton>
        </div>

        <ng-container *ngIf="!loadingApplications">
          <ng-container *ngIf="applications.length; else noApps">
            <div class="vp-list-item" *ngFor="let app of applications">
              <div class="vp-list-logo"><i class="pi pi-building"></i></div>
              <div class="vp-list-info">
                <p class="vp-list-title">{{ app.instituteName }}</p>
                <p class="vp-list-sub">
                  <span *ngIf="app.courseName">{{ app.courseName }}</span>
                  <span *ngIf="app.courseName && app.statusLabel"> · </span>
                  <span class="vp-status" *ngIf="app.statusLabel">{{ app.statusLabel }}</span>
                </p>
              </div>
            </div>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-btn vp-btn-outline"
              (click)="onViewApplications()">View Applications</button>
          </ng-container>
          <ng-template #noApps>
            <div class="vp-placeholder">
              <div class="vp-placeholder-emoji">📁</div>
              <p>Browse colleges and start your first application in just a few clicks.</p>
              <button *ngIf="resolvedCanEdit" type="button" class="vp-btn vp-btn-outline"
                (click)="onBrowseColleges()">Browse Colleges</button>
            </div>
          </ng-template>
        </ng-container>
      </div>

      <!-- ─── Saved Scholarships ──────────────────────────────────── -->
      <div class="vp-card">
        <div class="vp-card-head">
          <p class="vp-card-title">Saved Scholarships</p>
          <button *ngIf="resolvedCanEdit" type="button" class="vp-link-inline"
            (click)="onBrowseScholarships()">Browse</button>
        </div>

        <div class="vp-card-loading" *ngIf="loadingScholarships">
          <p-skeleton width="100%" height="2.4rem" styleClass="mb-2"></p-skeleton>
          <p-skeleton width="100%" height="2.4rem"></p-skeleton>
        </div>

        <ng-container *ngIf="!loadingScholarships">
          <ng-container *ngIf="scholarships.length; else noSch">
            <div class="vp-list-item" *ngFor="let s of scholarships">
              <div class="vp-list-logo vp-list-logo-star"><i class="pi pi-star-fill"></i></div>
              <div class="vp-list-info">
                <p class="vp-list-title">{{ s.scholarship.name }}</p>
                <p class="vp-list-sub">
                  <span *ngIf="s.scholarship.scholarshipAmount">{{ s.scholarship.scholarshipAmount }}</span>
                  <span *ngIf="scholarshipDeadline(s)"> · {{ scholarshipDeadline(s) }}</span>
                </p>
              </div>
            </div>
            <button *ngIf="resolvedCanEdit" type="button" class="vp-btn vp-btn-outline"
              (click)="onBrowseScholarships()">Browse Scholarships</button>
          </ng-container>
          <ng-template #noSch>
            <div class="vp-placeholder">
              <div class="vp-placeholder-emoji">⭐</div>
              <p>No scholarships saved. Start exploring to find opportunities.</p>
              <button *ngIf="resolvedCanEdit" type="button" class="vp-btn vp-btn-outline"
                (click)="onBrowseScholarships()">Browse Scholarships</button>
            </div>
          </ng-template>
        </ng-container>
      </div>
    </aside>
  </div>
</div>

<app-clarity-call-popup [(visible)]="showClarityCall"></app-clarity-call-popup>

<!-- ═══════════════════════ EDIT DIALOGS ═══════════════════════ -->

<!-- Personal details -->
<p-dialog header="Edit Personal Details" [(visible)]="showPersonalDialog" [modal]="true" [draggable]="false"
  styleClass="vp-dialog" [breakpoints]="{ '960px': '95vw' }" [style]="{ width: '720px' }">
  <app-edit-personal-details *ngIf="showPersonalDialog" [userId]="studentId || ''" (saved)="onPersonalSaved()"
    (photoUpdated)="onPhotoUpdated($event)" (close)="showPersonalDialog = false">
  </app-edit-personal-details>
</p-dialog>

<!-- Education -->
<p-dialog header="Edit Education" [(visible)]="showEducationDialog" [modal]="true" [draggable]="false"
  styleClass="vp-dialog" [breakpoints]="{ '960px': '95vw' }" [style]="{ width: '820px' }" (onHide)="loadEducation()">
  <app-education-info *ngIf="showEducationDialog" [parentForm]="hostForm"
    (stepSaved)="loadEducation()"></app-education-info>
</p-dialog>

<!-- Test Scores -->
<p-dialog header="Edit Test Scores" [(visible)]="showTestScoresDialog" [modal]="true" [draggable]="false"
  styleClass="vp-dialog" [breakpoints]="{ '960px': '95vw' }" [style]="{ width: '820px' }" (onHide)="loadTestScores()">
  <app-test-scores *ngIf="showTestScoresDialog" [parentForm]="hostForm"
    (stepSaved)="onTestScoresSaved()"></app-test-scores>
  <ng-template pTemplate="footer">
    <button type="button" class="vp-btn vp-btn-primary vp-dialog-save" (click)="saveTestScores()">Save Test
      Scores</button>
  </ng-template>
</p-dialog>

<!-- Experience -->
<p-dialog header="Edit Experience" [(visible)]="showExperienceDialog" [modal]="true" [draggable]="false"
  styleClass="vp-dialog" [breakpoints]="{ '960px': '95vw' }" [style]="{ width: '820px' }" (onHide)="loadExperience()">
  <app-experiences *ngIf="showExperienceDialog" [parentForm]="hostForm"
    (stepSaved)="loadExperience()"></app-experiences>
</p-dialog>

<!-- Recommendations -->
<p-dialog header="Edit Recommendations" [(visible)]="showRecommendationsDialog" [modal]="true" [draggable]="false"
  styleClass="vp-dialog" [breakpoints]="{ '960px': '95vw' }" [style]="{ width: '820px' }"
  (onHide)="loadRecommendations()">
  <app-recommendations *ngIf="showRecommendationsDialog" [parentForm]="hostForm"
    (stepSaved)="loadRecommendations()"></app-recommendations>
</p-dialog>

<!-- Awards & Accolades -->
<p-dialog header="Edit Awards &amp; Accolades" [(visible)]="showAwardsDialog" [modal]="true" [draggable]="false"
  styleClass="vp-dialog" [breakpoints]="{ '960px': '95vw' }" [style]="{ width: '820px' }" (onHide)="loadAwards()">
  <app-awards-accolades *ngIf="showAwardsDialog" [parentForm]="hostForm"
    (stepSaved)="loadAwards()"></app-awards-accolades>
</p-dialog>